import { useEffect, useRef, useState } from 'react'
import { Camera, Loader2, Trash2 } from 'lucide-react'
import { cn } from '@/shared/utils/cn'
import { useUploadFile } from '@/shared/hooks/useUploadFile'

const ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp'] as const
const MAX_BYTES = 5 * 1024 * 1024 // 5 MB

interface LogoUploadFieldProps {
  /** URL pública del logo (la presigned URL "pelada"). `null`/`''` cuando no hay logo. */
  value?: string | null
  onChange: (url: string | null) => void
  disabled?: boolean
  /** Label opcional sobre el botón. Default: `Logo`. */
  label?: string
}

function getExtension(file: File): string {
  return file.name.split('.').pop()?.toLowerCase() ?? ''
}

/**
 * Campo circular para subir el logo de la organización o del negocio. Muestra
 * preview local mientras se sube, conserva el preview tras la subida, y guarda
 * la URL pública del archivo en el form via `onChange`.
 *
 * Validación cliente: extensión en `ALLOWED_EXTENSIONS`, tamaño ≤ 5 MB.
 */
export function LogoUploadField({
  value,
  onChange,
  disabled,
  label = 'Logo',
}: LogoUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [localPreview, setLocalPreview] = useState<string | null>(null)
  const [validationError, setValidationError] = useState<string | null>(null)
  const { upload, uploading } = useUploadFile({ documentType: 'IMAGE' })

  // Libera el ObjectURL anterior cuando el componente desmonta o cambia.
  useEffect(() => {
    return () => {
      if (localPreview && localPreview.startsWith('blob:')) {
        URL.revokeObjectURL(localPreview)
      }
    }
  }, [localPreview])

  // `value` ya es la URL pública (o vacío si el usuario quitó el logo). El
  // preview local toma precedencia mientras se sube un archivo nuevo.
  const display = localPreview ?? (value && /^https?:/.test(value) ? value : null)

  const openPicker = () => {
    if (disabled || uploading) return
    inputRef.current?.click()
  }

  const handleSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // permite re-seleccionar el mismo archivo
    if (!file) return

    setValidationError(null)

    const ext = getExtension(file)
    if (!file.type.startsWith('image/') || !(ALLOWED_EXTENSIONS as readonly string[]).includes(ext)) {
      setValidationError('Formato no permitido. Usa PNG, JPG o WEBP.')
      return
    }
    if (file.size > MAX_BYTES) {
      setValidationError('La imagen supera los 5 MB.')
      return
    }

    const objectUrl = URL.createObjectURL(file)
    setLocalPreview(objectUrl)

    const url = await upload(file)
    if (url) {
      onChange(url)
    } else {
      // Falló: limpia preview para reflejar el estado real del form.
      URL.revokeObjectURL(objectUrl)
      setLocalPreview(null)
    }
  }

  const handleRemove = () => {
    if (disabled || uploading) return
    if (localPreview && localPreview.startsWith('blob:')) URL.revokeObjectURL(localPreview)
    setLocalPreview(null)
    setValidationError(null)
    onChange(null)
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <span className="text-sm font-medium text-ink">{label}</span>
      <button
        type="button"
        onClick={openPicker}
        disabled={disabled || uploading}
        aria-label={display ? 'Cambiar logo' : 'Subir logo'}
        className={cn(
          'relative grid h-24 w-24 place-items-center overflow-hidden rounded-full border-2 border-dashed bg-surface transition',
          display ? 'border-line' : 'border-line text-ink-subtle',
          !disabled && !uploading && 'hover:border-brand hover:text-brand',
          (disabled || uploading) && 'cursor-not-allowed opacity-70',
        )}
      >
        {display ? (
          <img src={display} alt="Logo" className="h-full w-full object-cover" />
        ) : (
          <Camera className="h-7 w-7" aria-hidden="true" />
        )}
        {uploading && (
          <span className="absolute inset-0 grid place-items-center bg-black/50">
            <Loader2 className="h-5 w-5 animate-spin text-white" aria-hidden="true" />
          </span>
        )}
      </button>

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={handleSelect}
        className="sr-only"
      />

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={openPicker}
          disabled={disabled || uploading}
          className="text-xs font-medium text-brand hover:text-brand-dark disabled:opacity-50"
        >
          {uploading ? 'Subiendo…' : display ? 'Cambiar' : 'Subir logo'}
        </button>
        {display && !uploading && (
          <button
            type="button"
            onClick={handleRemove}
            disabled={disabled}
            className="inline-flex items-center gap-1 text-xs font-medium text-ink-muted hover:text-danger disabled:opacity-50"
          >
            <Trash2 className="h-3 w-3" aria-hidden="true" />
            Quitar
          </button>
        )}
      </div>

      {validationError && (
        <p className="max-w-[180px] text-center text-xs text-danger">{validationError}</p>
      )}
      <p className="max-w-[180px] text-center text-[11px] text-ink-subtle">
        PNG, JPG o WEBP · máx 5 MB
      </p>
    </div>
  )
}
